import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	Inject,
	OnDestroy,
	OnInit,
	signal,
	viewChild,
} from '@angular/core';
import { BaseComponent } from '@core/base.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { dispatch, select, Store } from '@ngxs/store';
import Fuse from 'fuse.js';
import { startWith, tap } from 'rxjs/operators';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';
import { Menu } from 'src/app/ui/menu/menu.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Logger } from '@core/logger';
import { ContextAccessors, MatterFileType } from '@models/matter';
import { GroupType } from '@models/documents';
import {
	MatterPackageContentDocumentType,
	MatterPackageContentType,
} from '@models/matter-package';
import {
	DocumentCategory,
	MatterWordDocument,
} from '@models/matter-word-documents';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { NavigationService } from '@services/nav/nav.service';
import { MatterPackagesDispatch } from '@state/matter/matter-packages/matter-packages.dispatch';
import { MatterWordDocumentsDispatch } from '@state/matter/matter-word-documents/matter-word-documents.dispatch';
import { MatterWordDocumentWithMetadata } from '@state/matter/matter-word-documents/matter-word-documents.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { SetDocumentDrawerPreferences } from '@state/user-preferences/user-preferences.actions';
import { UserPreferencesState } from '@state/user-preferences/user-preferences.state';
import {
	LoadUserMattersUsage,
	ClearUserMattersUsage,
} from '@state/user-usage-matters/user-usage-matters.state';
import { BadgeComponent } from '@ui/badge/badge.component';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { DocListItemComponent } from 'src/app/ui/doc-list-item/doc-list-item.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { EmptyComponent } from 'src/app/ui/empty/empty.component';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import {
	TOGGLE_TYPES,
	ToggleComponent,
} from 'src/app/ui/toggle/toggle.component';
import { v4 as uuidv4 } from 'uuid';
import { DocumentsDrawerSelectors } from './documents-drawer.selectors';
import { CoreButtonPrimaryFillComponent, TestHookDirective } from '@core/ui';
import { TestHooks } from './documents-drawer.hooks';
import { Params } from '@angular/router';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { UploadedDocumentsOpenQueryParam } from '../../documents/uploaded/uploaded.component';
import { BusinessFeatureFlagSelectors } from 'src/app/shared/state/business';
import {
	UploadDocumentModalComponent,
	UploadModalDocumentData,
} from '../../documents/uploaded/modal/modal.component';
import { MatDialog } from '@angular/material/dialog';

interface ContextItem {
	referenceId: string;
	name: string;
}

interface DrawerDocument extends MatterWordDocumentWithMetadata {
	isAdded: boolean;
}

interface VM {
	categoriesMenu: Menu;
	addingDocIds: string[];
	expandedCategories: Record<string, boolean>;
}

const defaultVM: VM = {
	categoriesMenu: null,
	addingDocIds: [],
	expandedCategories: {},
};

interface CategoryItem {
	key: string;
	title: { label: string };
	children?: Array<{ key: string; title: { label: string } }>;
}

const CATEGORY_TO_CONTEXT_MAPPING: Record<string, string> = {
	[DocumentCategory.CLIENT]: ContextAccessors.CLIENTS, // 'client' -> 'clients'
	[DocumentCategory.COMPLIANCE_REQUEST]: ContextAccessors.JURISDICTIONS, // 'compliance request' -> 'jurisdictions'
	[DocumentCategory.DEBT]: ContextAccessors.DEBTS, // 'debt' -> 'debts'
	[DocumentCategory.ENCUMBRANCE]: ContextAccessors.ENCUMBRANCES, // 'encumbrance' -> 'encumbrances'
	[GroupType.MORTGAGE]: ContextAccessors.MORTGAGES, // 'mortgage' -> 'mortgages'
};

@UntilDestroy()
@Component({
	selector: 'app-documents-drawer',
	templateUrl: './documents-drawer.component.html',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FormsModule,
		IconsModule,
		ButtonComponent,
		MatTooltipModule,
		DocListItemComponent,
		EmptyComponent,
		BadgeComponent,
		ToggleComponent,
		TestHookDirective,
		CoreButtonPrimaryFillComponent,
	],
})
export class DocumentsDrawerComponent
	extends BaseComponent<VM>
	implements OnInit, OnDestroy
{
	testHooks = TestHooks;
	public DOCUMENT_CATEGORY = DocumentCategory;
	private matterId_ = select(SelectedMatter.id);
	private matterFileType_ = select(SelectedMatter.matterFileType);
	private matDialog = inject(MatDialog);
	TOGGLE_TYPES = TOGGLE_TYPES;

	private isPDFinPackagesEnabled_ = select(
		BusinessFeatureFlagSelectors.isFormsEnabledInPackages,
	);
	public uploadedUrl_ = computed(() =>
		UrlService.Matter_Documents_Uploaded(this.matterId_()),
	);
	public queryParams: Params = {
		open: UploadedDocumentsOpenQueryParam.UPLOAD,
	};

	searchValue_ = signal<string>('');
	selectedCategoryItem_ = signal<{
		parentKey: string;
		childKey?: string;
	} | null>({
		parentKey: DocumentCategory.MOST_USED,
		childKey: null,
	});

	documentDrawerPreferences = select(
		UserPreferencesState.getDocumentDrawerPreferences,
	);

	setDocumentDrawerPreferences = dispatch(SetDocumentDrawerPreferences);

	hideAddedDocuments_ = computed(
		() => this.documentDrawerPreferences()?.hideAddedDocuments ?? false,
	);

	sortByUsageWeight_ = computed(
		() =>
			!this.documentDrawerPreferences() ||
			this.documentDrawerPreferences()?.sortByUsageWeight,
	);

	selectedCategoryHasNoChildren_ = computed(() => {
		const selectedParentKey = this.selectedCategoryItem_()?.parentKey;
		if (selectedParentKey === DocumentCategory.MOST_USED) {
			return true;
		}
		const selectedCategory = this.VM?.categoriesMenu?.items?.find(
			(item) => item.key === selectedParentKey,
		);
		return (selectedCategory?.children?.length ?? 0) === 0;
	});

	loadUserMatters = dispatch(LoadUserMattersUsage);
	clearUserMatters = dispatch(ClearUserMattersUsage);
	getDocumentsDrawerData = select(
		DocumentsDrawerSelectors.getDocumentsDrawerData,
	);

	searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

	private contextData$ = inject(Store).select(
		DocumentsDrawerSelectors.getAllContextsData,
	);

	documentsDrawerData_ = computed(() => {
		return this.getDocumentsDrawerData()(this.data.packageId);
	});

	filteredContent_ = computed(() => {
		const { allDocuments, topAddedDocuments, topAvailableDocuments } =
			this.documentsDrawerData_();
		const sortByUsageWeight = this.sortByUsageWeight_();
		let filteredDocs = allDocuments;
		if (this.selectedCategoryItem_()) {
			const { parentKey, childKey } = this.selectedCategoryItem_();

			if (parentKey === DocumentCategory.MOST_USED) {
				const sortFunction = sortByUsageWeight
					? this.getSortByUsageWeightFunction()
					: this.getSortAlphabeticallyFunction();

				const sortedAddedDocs = [...topAddedDocuments].sort(sortFunction);
				const sortedAvailableDocs = [...topAvailableDocuments].sort(
					sortFunction,
				);

				return [...sortedAddedDocs, ...sortedAvailableDocs];
			}

			const parentItem = this.VM.categoriesMenu?.items?.find(
				(item) => item.key === parentKey,
			);

			if (parentItem) {
				filteredDocs = filteredDocs.filter((doc) => {
					if (!childKey) {
						if (
							doc.document.categories?.includes(parentKey as DocumentCategory)
						) {
							return true;
						}

						const contextKey = CATEGORY_TO_CONTEXT_MAPPING[parentKey];
						if (contextKey) {
							return doc.document.context?.[contextKey]?.length > 0;
						}

						return (
							doc.document.groupType !== null &&
							doc.document.groupType === (parentItem.key as GroupType)
						);
					}

					const contextKey = CATEGORY_TO_CONTEXT_MAPPING[parentItem.key];
					if (contextKey) {
						if (contextKey === ContextAccessors.JURISDICTIONS) {
							return (
								doc.document.context?.[contextKey]?.[0]?.referenceId ===
								childKey
							);
						}
						if (contextKey === ContextAccessors.CLIENTS) {
							return (
								doc.document.context?.[contextKey]?.[0]?.referenceId ===
								childKey
							);
						}

						return (
							doc.document.context?.[contextKey]?.[0]?.referenceId === childKey
						);
					}

					return false;
				});
			}
		}

		const addedDocs = filteredDocs
			.filter((doc) => doc.isAdded)
			.sort(this.getSortAlphabeticallyFunction());
		const availableDocs = filteredDocs
			.filter((doc) => !doc.isAdded)
			.sort(this.getSortAlphabeticallyFunction());
		return addedDocs.concat(availableDocs);
	});

	getAllDocuments_ = computed(() => {
		const filteredDocs = this.filteredContent_();
		let documents = this.filterBySearch(filteredDocs, this.searchValue_());

		if (this.hideAddedDocuments_()) {
			documents = documents.filter((doc) => !doc.isAdded);
		}
		return documents;
	});

	documentCount_ = computed(() => this.getAllDocuments_().length);

	private searchFormControl = new FormControl<string>('');
	private fuse: Fuse<DrawerDocument>;

	constructor(
		@Inject(DRAWER_DATA)
		private data: {
			packageId: string;
			onDocumentSelected: () => void;
		},
		private matterPackagesDispatch: MatterPackagesDispatch,
		private matterWordDocumentDispath: MatterWordDocumentsDispatch,
		private navService: NavigationService,
		private drawerService: DrawerService,
		private analytics: AnalyticsService,
	) {
		super(defaultVM);
		// focus when the search input is available
		effect(() => this.searchInput()?.nativeElement?.focus());
	}

	ngOnInit() {
		this.loadUserMatters();
		this.contextData$.pipe(untilDestroyed(this)).subscribe((contextData) => {
			const categoryMenuItems = [
				{
					key: DocumentCategory.MOST_USED,
					title: { label: 'Most Used' },
					isNew: true,
					dataIntercomTarget: 'most-used-documents-category',
				},
				{
					key: null,
					title: { label: 'All' },
				},
				{
					key: DocumentCategory.ACCOUNTING,
					title: { label: 'Accounting' },
				},
				{
					key: DocumentCategory.CLIENT,
					title: { label: 'Clients' },
					children: contextData.clients.map((client: ContextItem) => ({
						key: client.referenceId,
						title: {
							label: client.name,
						},
					})),
				},
				{
					key: DocumentCategory.COMMERCIAL,
					title: { label: 'Commercial' },
				},
				{
					key: DocumentCategory.COMPLIANCE_REQUEST,
					title: { label: 'Compliance Requests' },
					children: contextData.jurisdictions?.map((jurisdiction) => ({
						key: jurisdiction.referenceId,
						title: {
							label: jurisdiction.name,
						},
					})),
				},
				{
					key: DocumentCategory.CORPORATE,
					title: { label: 'Corporate' },
				},
				{
					key: DocumentCategory.DEBT,
					title: { label: 'Debts' },
					children: contextData.debts.map((debt) => ({
						key: debt.referenceId,
						title: {
							label: debt.name,
						},
					})),
				},
				{
					key: DocumentCategory.ENCUMBRANCE,
					title: { label: 'Encumbrances' },
					children: contextData.encumbrances.map((encumbrance) => ({
						key: encumbrance.referenceId,
						title: {
							label: encumbrance.name,
						},
					})),
					matterFileType: [MatterFileType.SALE, MatterFileType.REFINANCE],
				},
				{
					key: DocumentCategory.LETTER,
					title: { label: 'Letters' },
				},
				{
					key: GroupType.MORTGAGE,
					title: { label: 'Mortgages' },
					children: contextData.mortgages.map((mortgage) => ({
						key: mortgage.referenceId,
						title: {
							label: mortgage.name,
						},
					})),
				},
				{
					key: DocumentCategory.SIGNING_DOCUMENT,
					title: { label: 'Signing Documents' },
				},
				{
					key: DocumentCategory.WGD,
					title: { label: 'WGD' },
				},
				{
					key: DocumentCategory.UPLOAD,
					title: { label: 'Uploads' },
					isNew: true,
					dataIntercomTarget: 'uploads-documents-category',
				},
				{
					key: DocumentCategory.SYNC_FORMS,
					title: { label: 'Sync Forms' },
					isNew: true,
					// dataIntercomTarget: 'uploads-documents-category',
				},
				{
					key: DocumentCategory.MATTER_FORMS,
					title: { label: 'Matter Forms' },
					isNew: true,
					// dataIntercomTarget: 'uploads-documents-category',
				},
			].filter(
				(item) =>
					((item.key !== DocumentCategory.SYNC_FORMS &&
						item.key !== DocumentCategory.MATTER_FORMS) ||
						this.isPDFinPackagesEnabled_()) &&
					(!item.matterFileType ||
						item.matterFileType.includes(this.matterFileType_())),
			);

			this.VM = {
				...this.VM,
				categoriesMenu: {
					id: this.VM.categoriesMenu?.id || uuidv4(),
					items: categoryMenuItems,
					title: {
						label: 'Categories',
						iconName: 'adjustments-horizontal',
					},
				} as Menu,
			};
		});

		this.searchFormControl.valueChanges
			.pipe(
				startWith(''),
				untilDestroyed(this),
				tap((value) => {
					this.searchValue_.set(value || '');
				}),
			)
			.subscribe();
	}

	openUploadModal() {
		const data: UploadModalDocumentData = {
			matterId: this.matterId_(),
			runAiScraping: false,
		};
		this.matDialog.open(UploadDocumentModalComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});
	}

	onAddDocument(docMetadata: DrawerDocument) {
		Logger.info('onAddDocument called from documents-drawer', {
			docMetadata,
			source: this.data.packageId ? 'package' : 'documents',
		});
		if (docMetadata.isLoading || docMetadata.isAdded) {
			Logger.info('Document is loading or already added', {
				docMetadata,
			});
			return;
		}

		const defaultCategory = DocumentCategory.MOST_USED;

		if (this.data.packageId) {
			this.analytics.logEvent(
				AnalyticsEvent.MATTER_PACKAGE_DRAWER_SELECT_DOCUMENT_TO_ADD,
				{
					searchCharacterCount: this.searchValue_().length,
					filterCategory:
						this.selectedCategoryItem_()?.parentKey ?? defaultCategory,
				},
			);
		} else {
			this.analytics.logEvent(
				AnalyticsEvent.MATTER_DOCUMENTS_DRAWER_SELECT_DOCUMENT_TO_ADD,
				{
					searchCharacterCount: this.searchValue_().length,
					filterCategory:
						this.selectedCategoryItem_()?.parentKey ?? defaultCategory,
				},
			);
		}

		if (this.data.onDocumentSelected) {
			this.data.onDocumentSelected();
		}

		this.VM = {
			addingDocIds: [...this.VM.addingDocIds, docMetadata.document.id],
		};

		if (!this.data.packageId) {
			Logger.info('Document added from drawer from onAddDocument', {
				docMetadata,
				source: 'documents',
			});
			this.matterWordDocumentDispath.Create({
				document: docMetadata.document as MatterWordDocument,
			});
			return;
		}

		Logger.info('Document added from drawer from onAddDocument', {
			docMetadata,
			source: 'package',
		});

		let docType = MatterPackageContentType.MATTER_DOC;
		if (docMetadata.document?.categories?.includes(DocumentCategory.UPLOAD))
			docType = MatterPackageContentType.UPLOADED_FILE;
		if (docMetadata.document?.categories?.includes(DocumentCategory.SYNC_FORMS))
			docType = MatterPackageContentType.SYNC_FORM;
		if (
			docMetadata.document?.categories?.includes(DocumentCategory.MATTER_FORMS)
		)
			docType = MatterPackageContentType.MATTER_FORM;

		this.matterPackagesDispatch.AddContent({
			docMetadata: {
				id: this.data.packageId,
				title: docMetadata.document.title,
				type: docType,
				contentType: docMetadata.document?.sourceStorageRef?.includes('.docx')
					? MatterPackageContentDocumentType.WORD
					: MatterPackageContentDocumentType.PDF,
				content: docMetadata.document,
			},
			duplicated: false,
			generated: docMetadata.isGenerated,
		});
	}

	private initializeFuse(documents: DrawerDocument[]): void {
		this.fuse = new Fuse(documents, {
			keys: [
				'document.title',
				'document.categories',
				'document.groupType',
				'document.context.clients.name',
				'document.context.jurisdictions.name',
				'document.context.mortgages.name',
				'document.context.encumbrances.name',
				'document.context.debts.name',
				'document.alternativeNames',
			],
			threshold: 0.3,
			ignoreLocation: true,
			useExtendedSearch: true,
			includeMatches: true,
		});
	}

	private filterBySearch(
		documents: DrawerDocument[],
		searchValue: string,
	): DrawerDocument[] {
		if (!searchValue) return documents;

		this.initializeFuse(documents);
		const searchResults = this.fuse.search(searchValue);

		const matchedDocuments = new Map<string, string[]>();
		searchResults.forEach((result) => {
			const alternativeNameMatches =
				result.matches
					?.filter((match) => match.key === 'document.alternativeNames')
					.map((match) => match.value) || [];
			matchedDocuments.set(result.item.document.id, alternativeNameMatches);
		});

		return documents
			.filter((doc) => matchedDocuments.has(doc.document.id))
			.map((doc) => ({
				...doc,
				alternativeNameMatches: matchedDocuments.get(doc.document.id) || [],
			}));
	}

	trackByDocId(index: number, doc: DrawerDocument): string {
		return doc.document.id;
	}

	clearSearch(input: HTMLInputElement): void {
		this.searchFormControl.setValue('');
		input.focus();
	}

	toggleCategory(
		parentKey: string,
		childKey?: string,
		hasChildren?: boolean,
	): void {
		if (parentKey === null) {
			this.selectedCategoryItem_.set(null);
		} else {
			this.selectedCategoryItem_.set({ parentKey, childKey });
			if (hasChildren) {
				this.VM = {
					expandedCategories: {
						...this.VM.expandedCategories,
						[parentKey]: !this.VM.expandedCategories[parentKey],
					},
				};
			}
		}
	}

	isSelected(menuKey: string, key: string, childKey?: string): boolean {
		if (menuKey === 'category' && key === null) {
			return this.selectedCategoryItem_() === null;
		}
		if (childKey !== undefined) {
			return (
				this.selectedCategoryItem_()?.parentKey === key &&
				this.selectedCategoryItem_()?.childKey === childKey
			);
		}
		return (
			this.selectedCategoryItem_()?.parentKey === key &&
			!this.selectedCategoryItem_()?.childKey
		);
	}

	toggleCategoryChildren(category: CategoryItem): void {
		this.VM = {
			...this.VM,
			expandedCategories: {
				...this.VM.expandedCategories,
				[category.key]: !this.VM.expandedCategories[category.key],
			},
		};
	}

	onAddJurisdiction() {
		this.navService.goToMatter_Property_Jurisdictions(this.matterId_());
		this.closerDrawer();
	}

	onAddMortgage() {
		this.navService.goToMatter_Mortgages(this.matterId_());
		this.closerDrawer();
	}

	onAddClient() {
		this.navService.goToMatter_Parties(this.matterId_());
		this.closerDrawer();
	}

	onAddEncumbrance() {
		this.navService.goToMatter_Mortgages_Encumbrances(this.matterId_());
		this.closerDrawer();
	}

	onAddBillOrDebt() {
		this.navService.goToMatter_Debts(this.matterId_());
		this.closerDrawer();
	}

	toggleHideAddedDocuments(): void {
		const newValue = !this.hideAddedDocuments_();
		this.setDocumentDrawerPreferences({
			hideAddedDocuments: newValue,
		});
	}

	toggleSortByUsageWeight(): void {
		const newValue = !this.sortByUsageWeight_();
		this.setDocumentDrawerPreferences({
			sortByUsageWeight: newValue,
		});
	}

	closerDrawer() {
		this.drawerService.closeDrawer();
	}

	private getSortByUsageWeightFunction() {
		const weightedDocs = this.documentsDrawerData_().allDocuments;
		const sourceToIndex = new Map(
			weightedDocs.map((doc, index) => [doc.document.source, index]),
		);

		return (a: DrawerDocument, b: DrawerDocument) => {
			const indexA =
				sourceToIndex.get(a.document.source) ?? Number.MAX_SAFE_INTEGER;
			const indexB =
				sourceToIndex.get(b.document.source) ?? Number.MAX_SAFE_INTEGER;
			return indexA - indexB;
		};
	}

	private getSortAlphabeticallyFunction() {
		return (a: DrawerDocument, b: DrawerDocument) =>
			a.document.title.localeCompare(b.document.title);
	}

	ngOnDestroy() {
		this.clearUserMatters();
	}
}
